import React, { lazy, Suspense, useState } from 'react'
import { Box, CircularProgress, IconButton, Tooltip, SxProps, Theme } from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Vehicle } from '@otw/models'
import {
  soldDialogShippingFromVehicle,
  storedShippingFromDialog,
} from '@/components/inventory/dialogs/soldShipping'
import { DocumentData } from 'firebase/firestore'

const LazyEditVehicleForm = lazy(() => import('@/components/inventory/forms/EditVehicleForm'))
const LazySoldDialog = lazy(() => import('@/components/inventory/dialogs/SoldDialog'))
const LazyDateAvailabilityDialog = lazy(() => import('@/components/inventory/dialogs/DateAvailabilityDialog'))
import { useInventoryWrite } from 'hooks/inventory'
import { useSnackbar } from '@/hooks/common/useSnackbar'

interface VehicleEditButtonProps {
  vehicle: Vehicle
  inventoryCollection?: 'master_inventory' | 'shield_inventory'
  color?: string
  size?: 'small' | 'medium' | 'large'
  tooltip?: string
  sx?: SxProps<Theme>
  /**
   * Callback fired when a vehicle edit is successfully completed
   */
  onEditComplete?: () => void
}

/**
 * A reusable button component for editing vehicles
 */
const VehicleEditButton: React.FC<VehicleEditButtonProps> = ({
  vehicle,
  inventoryCollection = 'master_inventory',
  color,
  size = 'small',
  tooltip = 'Edit Vehicle',
  sx,
  onEditComplete,
}) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [soldPopupOpen, setSoldPopupOpen] = useState(false)
  const [openDateAvailableDialog, setOpenDateAvailableDialog] = useState(false)
  const [pendingVehicleUpdate, setPendingVehicleUpdate] = useState<{
    id: string
    data: any
  } | null>(null)
  const { updateDocument } = useInventoryWrite(inventoryCollection)
  const { showSuccess, showError } = useSnackbar()

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent parent click handlers from firing
    setEditDialogOpen(true)
  }

  const handleDialogClose = () => {
    setEditDialogOpen(false)
  }

  const handleEditSuccess = () => {
    setEditDialogOpen(false)
    // Notify parent component if provided
    if (onEditComplete) onEditComplete()
  }

  // Helper function to update the document
  const updateVehicleDocument = async (vehicleId: string, data: any): Promise<boolean> => {
    try {
      await updateDocument({
        id: vehicleId,
        data: data as Partial<DocumentData>,
      })
      showSuccess('Vehicle updated successfully')
      // Notify parent component if provided
      if (onEditComplete) onEditComplete()
      return true
    } catch (error) {
      console.error('Error updating vehicle:', error)
      showError('Error updating vehicle')
      return false
    }
  }

  // Handler for the sold dialog submission
  const handleSoldSubmit = async (
    soldToUser: { id: string; name: string },
    soldByUser: { id: string; name: string },
    location: string,
    warrantyInfo?: {
      warrantyTerms: string | null
      warrantyCost: number | null
      customerCost: number | null
      warrantyProfit: number | null
    } | null,
    financeInfo?: {
      paymentType: string
      downPayment: number | null
      bankName: string | null
      amountFinanced: number | null
      points: number | null
      profit: number | null
      totalSalePrice: number | null
      taxRate: number | null
      includeTaxInSale: boolean
      includeWarrantyInSale: boolean
      includeShippingInSale: boolean
      transleaseLease?: boolean
    } | null,
    shippingInfo?: {
      shipped: boolean | null
      ourShippingCost: number | null
      customerShippingCost: number | null
      profit: number | null
    } | null,
    funded?: boolean,
  ) => {
    if (!pendingVehicleUpdate) return

    const soldToUserData =
      soldToUser?.id && soldToUser?.name
        ? {
            id: String(soldToUser.id),
            name: String(soldToUser.name),
          }
        : { id: '', name: '' }

    const soldByUserData =
      soldByUser?.id && soldByUser?.name
        ? {
            id: String(soldByUser.id),
            name: String(soldByUser.name),
          }
        : { id: 'house', name: 'House' }

    const today = new Date()
    today.setHours(12, 0, 0, 0) // Set to noon local time
    const todayIsoString = today.toISOString()

    const finalData = {
      ...pendingVehicleUpdate.data,
      sold_to_user: soldToUserData,
      sold_by_user: soldByUserData,
      sold_location: location,
      sold_date: todayIsoString,
      show_on_website: false,
      status: 'Sold',
    }

    if (warrantyInfo) {
      finalData['warranty_purchaged'] = true
      finalData['warranty'] = {
        warranty_terms: warrantyInfo.warrantyTerms,
        warranty_cost: warrantyInfo.warrantyCost,
        customer_cost: warrantyInfo.customerCost,
        warranty_created_at: new Date().toISOString(),
        warranty_profit: warrantyInfo.warrantyProfit,
      }
    }

    // Add finance information
    if (financeInfo) {
      // Create finance object with payment_type included
      finalData['finance'] = {
        payment_type: financeInfo.paymentType,
        down_payment: financeInfo.downPayment !== null ? Number(financeInfo.downPayment) : null,
        bank_name: financeInfo.bankName,
        amount_financed: financeInfo.amountFinanced !== null ? Number(financeInfo.amountFinanced) : null,
        points: financeInfo.points !== null ? Number(financeInfo.points) : null,
        profit: financeInfo.profit !== null ? Number(financeInfo.profit) : null,
        total_sale_price: financeInfo.totalSalePrice !== null ? Number(financeInfo.totalSalePrice) : null,
        tax_rate: financeInfo.taxRate !== null ? Number(financeInfo.taxRate) : null,
        include_tax_in_sale: financeInfo.includeTaxInSale,
        include_warranty_in_sale: financeInfo.includeWarrantyInSale,
        include_shipping_in_sale: financeInfo.includeShippingInSale,
      }
      finalData['translease_lease'] = Boolean(financeInfo.transleaseLease)
    }

    const storedShipping = storedShippingFromDialog(shippingInfo)
    if (storedShipping) {
      finalData['shipping'] = storedShipping
    }

    if (funded !== undefined) {
      finalData['funded'] = funded
      if (funded) {
        finalData['funded_date'] = vehicle.funded_date ?? new Date().toISOString()
      } else {
        finalData['funded_date'] = null
      }
    }

    await updateVehicleDocument(pendingVehicleUpdate.id, finalData)
    setSoldPopupOpen(false)
    setPendingVehicleUpdate(null)
  }

  // Handle date availability dialog confirmation
  const handleDateAvailabilityConfirm = async (date: string | null) => {
    if (!pendingVehicleUpdate || !date) {
      // If no date was provided, cancel the operation
      setOpenDateAvailableDialog(false)
      setPendingVehicleUpdate(null)
      return
    }

    // Update the data with the new date
    const finalData = {
      ...pendingVehicleUpdate.data,
      date_available: date,
      status: 'Pending Available',
    }

    await updateVehicleDocument(pendingVehicleUpdate.id, finalData)
    setOpenDateAvailableDialog(false)
    setPendingVehicleUpdate(null)
  }

  const handleStatusChange = (vehicleId: string, data: any, status: string) => {
    // Store data for dialog handling
    setPendingVehicleUpdate({
      id: vehicleId,
      data: data,
    })

    // Based on status, show appropriate dialog
    if (status.toLowerCase() === 'sold') {
      setSoldPopupOpen(true)
    } else if (status.toLowerCase() === 'pending available') {
      setOpenDateAvailableDialog(true)
    }
  }

  if (!vehicle?.id) {
    return null
  }

  const showInventoryUi = editDialogOpen || soldPopupOpen || openDateAvailableDialog

  return (
    <>
      <Tooltip title={tooltip}>
        <IconButton size={size} onClick={handleEditClick} sx={{ color: color || 'primary.main', ...sx }}>
          <EditOutlinedIcon fontSize={size} />
        </IconButton>
      </Tooltip>

      {showInventoryUi ? (
        <Suspense
          fallback={
            <Box sx={{ display: 'none' }} aria-hidden>
              <CircularProgress size={24} />
            </Box>
          }
        >
          {/* Edit Form Dialog */}
          {editDialogOpen ? (
        <LazyEditVehicleForm
          open={editDialogOpen}
          vehicleId={vehicle.id}
          onClose={handleDialogClose}
          onEditSuccess={handleEditSuccess}
          inventoryCollection={inventoryCollection}
          onStatusChange={handleStatusChange}
        />
          ) : null}

          {/* Sold Dialog */}
          {soldPopupOpen ? (
      <LazySoldDialog
        open={soldPopupOpen}
        onClose={() => {
          setSoldPopupOpen(false)
          setPendingVehicleUpdate(null)
        }}
        onSubmit={handleSoldSubmit}
        itemName={vehicle?.stock_number || vehicle?.id}
        price={vehicle?.optic_list_price || 0}
        currentLocation={vehicle?.location || undefined}
        initialData={{
          soldToUser: vehicle?.sold_to_user || undefined,
          soldByUser: vehicle?.sold_by_user || undefined,
          soldLocation: vehicle?.sold_location || undefined,
          warranty: vehicle?.warranty
            ? {
                warrantyTerms: vehicle.warranty.warranty_terms,
                warrantyCost: vehicle.warranty.warranty_cost,
                customerCost: vehicle.warranty.customer_cost,
                warrantyProfit: vehicle.warranty.warranty_profit,
              }
            : undefined,
          finance: vehicle?.finance
            ? {
                paymentType: vehicle.finance.payment_type,
                downPayment: vehicle.finance.down_payment,
                bankName: vehicle.finance.bank_name,
                amountFinanced: vehicle.finance.amount_financed,
                points: vehicle.finance.points,
                profit: vehicle.finance.profit,
                totalSalePrice: vehicle.finance.total_sale_price,
                taxRate: vehicle.finance.tax_rate,
                includeTaxInSale: vehicle.finance.include_tax_in_sale,
                includeWarrantyInSale: vehicle.finance.include_warranty_in_sale,
                includeShippingInSale: vehicle.finance.include_shipping_in_sale,
                transleaseLease: Boolean(vehicle.translease_lease),
              }
            : {
                paymentType: 'cash' as const,
                downPayment: null,
                bankName: null,
                amountFinanced: null,
                points: null,
                profit: 0,
                totalSalePrice: 0,
                taxRate: null,
                includeTaxInSale: false,
                includeWarrantyInSale: false,
                includeShippingInSale: false,
                transleaseLease: Boolean(vehicle?.translease_lease),
              },
          shipping: soldDialogShippingFromVehicle(vehicle?.shipping),
          funded: Boolean(vehicle?.funded) || Boolean(vehicle?.funded_date),
          fundedDate: vehicle?.funded_date ?? null,
        }}
      />
          ) : null}

          {/* Date Availability Dialog */}
          {openDateAvailableDialog ? (
      <LazyDateAvailabilityDialog
        open={openDateAvailableDialog}
        onClose={() => {
          setOpenDateAvailableDialog(false)
          setPendingVehicleUpdate(null)
        }}
        onConfirm={handleDateAvailabilityConfirm}
        currentDate={pendingVehicleUpdate?.data?.date_available || null}
        vehicleId={pendingVehicleUpdate?.id}
        vehicleInfo={pendingVehicleUpdate ? `Vehicle ID: ${pendingVehicleUpdate.id}` : undefined}
      />
          ) : null}
        </Suspense>
      ) : null}
    </>
  )
}

export default VehicleEditButton
